export const ROLES = {
  ADMIN: "ADMIN",
  MARKETING: "MARKETING",
  CLAIM: "CLAIM",
  DS: "DS",
  CALL_CENTER: "CALL_CENTER",
  PS: "PS",
  UW: "UW",
  PC: "PC",
};

export const SECTION_ORDER = ["Content", "Insurance", "Users", "Support"];

/** Offline fallback catalog when API is unavailable (system modules only). */
export const FALLBACK_CATALOG = [
  { key: "newsPosts", label: "News Posts", section: "Content", basePath: "/all_news", iconKey: "news", adminOnly: false, active: true },
  { key: "knowledges", label: "Knowledges", section: "Content", basePath: "/knowledges", iconKey: "knowledge", adminOnly: false, active: true },
  { key: "privileges", label: "Privilege", section: "Content", basePath: "/privileges", iconKey: "privilege", adminOnly: false, active: true },
  { key: "banners", label: "Banners", section: "Content", basePath: "/banners", iconKey: "onboarding", adminOnly: false, active: true },
  { key: "emergencies", label: "Emergencies", section: "Content", basePath: "/emergencies", iconKey: "emergency", adminOnly: false, active: true },
  { key: "onboardings", label: "Onboardings", section: "Content", basePath: "/onboardings", iconKey: "onboarding", adminOnly: false, active: true },
  { key: "claimFiles", label: "Claim Files", section: "Insurance", basePath: "/claimFiles", iconKey: "claimForm", adminOnly: false, active: true },
  { key: "loanFiles", label: "Loan Files", section: "Insurance", basePath: "/loanFiles", iconKey: "claimForm", adminOnly: false, active: true },
  { key: "policyDocuments", label: "Policy Documents", section: "Insurance", basePath: "/policy-documents", iconKey: "claimForm", adminOnly: false, active: true },
  { key: "prestigeCard", label: "Prestige Card", section: "Insurance", basePath: "/prestige-cards", iconKey: "onboarding", adminOnly: false, active: true },
  { key: "userLevel2", label: "User Level 2", section: "Users", basePath: "/users2", iconKey: "user", adminOnly: false, active: true },
  { key: "userLevel3", label: "User Level 3", section: "Users", basePath: "/users3", iconKey: "user", adminOnly: false, active: true },
  { key: "companyUser", label: "Company User", section: "Users", basePath: "/companys", iconKey: "user", adminOnly: false, active: true },
  { key: "dashboardUsers", label: "Dashboard Users", section: "Users", basePath: "/dashboard-users", iconKey: "manageAccounts", adminOnly: true, active: true },
  { key: "rolePermissions", label: "Role Permissions", section: "Users", basePath: "/role-permissions", iconKey: "adminPanel", adminOnly: true, active: true },
  { key: "dashboardModules", label: "Dashboard Modules", section: "Users", basePath: "/dashboard-modules", iconKey: "adminPanel", adminOnly: true, active: true },
  { key: "inquiry", label: "Inquiry", section: "Support", basePath: "/inquiry", iconKey: "inquiry", adminOnly: false, active: true },
  { key: "faqs", label: "FAQs", section: "Support", basePath: "/faqs", iconKey: "liveHelp", adminOnly: false, active: true },
  { key: "popup", label: "Popup", section: "Support", basePath: "/popup-messages", iconKey: "liveHelp", adminOnly: false, active: true },
  { key: "userManual", label: "User Manual", section: "Support", basePath: "/user-manual", iconKey: "onboarding", adminOnly: false, active: true },
  { key: "bankInformation", label: "Bank Information", section: "Support", basePath: "/bank-infos", iconKey: "onboarding", adminOnly: false, active: true },
];

/** Legacy / seed defaults — used only when API has not returned modules yet. */
export const ROLE_MODULES = {
  [ROLES.ADMIN]: FALLBACK_CATALOG.map((m) => m.key),
  [ROLES.MARKETING]: [
    "newsPosts",
    "knowledges",
    "privileges",
    "emergencies",
    "popup",
  ],
  [ROLES.CLAIM]: ["claimFiles", "loanFiles"],
  [ROLES.DS]: ["prestigeCard", "userLevel2", "inquiry", "userManual"],
  [ROLES.CALL_CENTER]: ["userLevel2", "inquiry"],
  [ROLES.PS]: [
    "knowledges",
    "banners",
    "onboardings",
    "policyDocuments",
    "faqs",
    "popup",
  ],
  [ROLES.UW]: ["policyDocuments"],
  [ROLES.PC]: ["bankInformation"],
};

export const getCatalogKeys = (catalog) => {
  const list = Array.isArray(catalog) && catalog.length ? catalog : FALLBACK_CATALOG;
  return list.filter((m) => m.active !== false).map((m) => m.key);
};

/**
 * Resolve accessible module keys for a session.
 * ADMIN always gets the full active catalog.
 */
export const resolveModuleKeys = (role, modulesFromApi, catalog) => {
  const catalogKeys = getCatalogKeys(catalog);

  if (role === ROLES.ADMIN) {
    return catalogKeys;
  }
  if (Array.isArray(modulesFromApi)) {
    const allowed = new Set(catalogKeys);
    return modulesFromApi.filter((key) => allowed.has(key));
  }
  if (modulesFromApi === undefined) {
    return ROLE_MODULES[role] || [];
  }
  return [];
};

export const hasModuleAccess = (moduleKeys, moduleKey) =>
  Array.isArray(moduleKeys) && moduleKeys.includes(moduleKey);

export const getModulesForKeys = (
  moduleKeys,
  catalog,
  registeredModuleKeys = null
) => {
  const list = Array.isArray(catalog) && catalog.length ? catalog : FALLBACK_CATALOG;
  const byKey = Object.fromEntries(list.map((m) => [m.key, m]));
  const registered =
    registeredModuleKeys instanceof Set
      ? registeredModuleKeys
      : new Set(registeredModuleKeys || []);

  const keys = Array.isArray(moduleKeys) ? moduleKeys : [];
  return keys
    .map((key) => {
      const item = byKey[key];
      if (!item || item.active === false) return null;
      if (registered.size > 0 && !registered.has(key)) return null;
      return {
        key,
        label: item.label,
        section: item.section,
        to: item.basePath,
        iconKey: item.iconKey,
      };
    })
    .filter(Boolean);
};

export const getDefaultRoute = (
  moduleKeys,
  catalog,
  registeredModuleKeys = null
) => {
  const modules = getModulesForKeys(moduleKeys, catalog, registeredModuleKeys);
  return modules[0]?.to || "/login";
};

/** Admin can edit profile; other roles cannot (matches prior marketing restriction). */
export const canEditProfile = (role) => role === ROLES.ADMIN;

export const getAssignableCatalog = (catalog) => {
  const list = Array.isArray(catalog) && catalog.length ? catalog : FALLBACK_CATALOG;
  return list.filter((m) => m.active !== false && !m.adminOnly);
};
